"use client"

import React, { useState } from "react"
import { cn } from "../lib/utils"

// Mock UI components
const Dialog = ({ children, open, onOpenChange, ...props }: any) => {
  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" {...props}>
      <div className="bg-zinc-900 rounded-lg shadow-lg max-w-md w-full">
        {children}
        <div className="absolute top-0 right-0 p-4">
          <button onClick={() => onOpenChange(false)} className="text-zinc-400 hover:text-white focus:outline-none">
            <svg
              width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
              className="w-6 h-6"
            >
              <path d="M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  )
}

const DialogContent = ({ children, className, ...props }: any) => (
  <div className={cn("p-6", className)} {...props}>
    {children}
  </div>
)

const DialogHeader = ({ children, ...props }: any) => (
  <div className="mb-4" {...props}>
    {children}
  </div>
)

const DialogTitle = ({ children, className, ...props }: any) => (
  <h2 className={cn("text-xl font-bold", className)} {...props}>
    {children}
  </h2>
)

const DialogDescription = ({ children, className, ...props }: any) => (
  <p className={cn("text-zinc-400 text-sm", className)} {...props}>
    {children}
  </p>
)

const DialogFooter = ({ children, ...props }: any) => (
  <div className="mt-6 flex justify-end gap-2" {...props}>
    {children}
  </div>
)

const Label = ({ children, htmlFor, className, ...props }: any) => (
  <label htmlFor={htmlFor} className={cn("text-sm font-medium block mb-1", className)} {...props}>
    {children}
  </label>
)

const Select = ({ children, value, onValueChange, className, ...props }: any) => {
  return (
    <div className={cn("relative", className)} {...props}>
      <select
        value={value}
        onChange={(e) => onValueChange(e.target.value)}
        className="w-full bg-zinc-800 border border-zinc-700 rounded p-2 text-white appearance-none"
      >
        {React.Children.map(children, (child) => {
          if (React.isValidElement(child) && child.type === SelectItem) {
            const childElement = child as React.ReactElement<{ value: string; children: React.ReactNode }>
            return <option value={childElement.props.value}>{childElement.props.children}</option>
          }
          return null
        })}
      </select>
      <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M2.5 4.5L6 8L9.5 4.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    </div>
  )
}

const SelectContent = ({ children, ...props }: any) => null
const SelectItem = ({ value, children, ...props }: any) => null
const SelectTrigger = ({ children, ...props }: any) => null
const SelectValue = ({ placeholder, ...props }: any) => null

const Switch = ({ checked, onCheckedChange, className, id, ...props }: any) => (
  <button
    role="switch"
    aria-checked={checked}
    id={id}
    onClick={() => onCheckedChange(!checked)}
    className={cn(
      "relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none",
      checked ? "bg-emerald-600" : "bg-zinc-600",
      className,
    )}
    {...props}
  >
    <span
      className={cn(
        "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
        checked ? "translate-x-6" : "translate-x-1",
      )}
    />
  </button>
)

const Button = ({ children, className, onClick, type, ...props }: any) => (
  <button
    className={cn("px-4 py-2 rounded font-medium focus:outline-none", className)}
    onClick={onClick}
    type={type}
    {...props}
  >
    {children}
  </button>
)

interface SettingsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function SettingsDialog({ open, onOpenChange }: SettingsDialogProps) {
  const [model, setModel] = useState("gpt-4o")
  const [temperature, setTemperature] = useState("balanced")
  const [streaming, setStreaming] = useState(true)
  const [darkMode, setDarkMode] = useState(true)
  const [notifications, setNotifications] = useState(false)
  const [lsMode, setLsMode] = useState(false)
  const [requestsUsedPercentage, setRequestsUsedPercentage] = useState(50)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px] bg-zinc-900 text-white border-zinc-700">
        <DialogHeader>
          <DialogTitle className="text-xl">Settings</DialogTitle>
          <DialogDescription className="text-zinc-400">Customize your AI chat experience</DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="lsMode">LS Mode</Label>
              <p className="text-sm text-zinc-400">When enabled, the AI will extoll the virtues of the LS engine.</p>
            </div>
            <Switch
              id="lsMode"
              checked={lsMode}
              onCheckedChange={setLsMode}
              className="data-[state=checked]:bg-emerald-600"
            />
          </div>

          <div className="space-y-2">
            <Label>Requests Used</Label>
            <div className="w-full bg-zinc-700 rounded-full h-2">
              <div
                className="bg-emerald-600 h-2 rounded-full"
                style={{ width: `${requestsUsedPercentage}%` }}
              ></div>
            </div>
            <p className="text-sm text-zinc-400">{requestsUsedPercentage}% of requests used this month</p>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="submit"
            onClick={() => onOpenChange(false)}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
